import React, { useMemo } from 'react';
import { isSameDay, startOfDay } from 'date-fns';

import type {
	ClassName,
	TileClassNameFunc,
	TileContentFunc,
	TileDisabledFunc,
} from './shared/types';
import { cn } from '@fshr/shared-react';
import { getRTL } from '@micro-apps/env';

type TileProps = {
	activeStartDate: Date;
	children: React.ReactNode;
	classes?: string[];
	date: Date;
	formatAbbr?: (locale: string, date: Date) => string;
	locale: string;
	maxDate?: Date;
	minDate?: Date;
	onClick?: (date: Date, event: React.MouseEvent<HTMLButtonElement>) => void;
	style?: React.CSSProperties;
	tileClassName?: TileClassNameFunc | ClassName;
	tileContent?: TileContentFunc | React.ReactNode;
	tileDisabled?: TileDisabledFunc;
	tileAriaLabel?: (d: Date) => string;
	isSelected?: boolean;
	isSameDaySelection?: boolean;
	isRestricted?: boolean;
	isAvailable?: boolean;
	allowUnavailableSelection?: boolean;
	nightlyRatesExperiment?: boolean;
	isMobile?: boolean;
};

export default function Tile(props: TileProps) {
	const {
		activeStartDate,
		children,
		classes,
		date,
		formatAbbr,
		locale,
		maxDate,
		minDate,
		onClick,
		style,
		tileClassName: tileClassNameProps,
		tileContent: tileContentProps,
		tileDisabled,
		tileAriaLabel,
		isSelected,
		isSameDaySelection,
		isRestricted,
		isAvailable,
		allowUnavailableSelection = false,
		nightlyRatesExperiment,
		isMobile,
	} = props;

	const isRangeSelection = isSelected && !isSameDaySelection;
	const useMobileRangeLayout = isMobile && isRangeSelection;

	// When the nightly rates experiment is ON the selection must be fully squared.
	// The full variant-prefixed token must live inside each literal so Tailwind's
	// content scanner emits the corresponding CSS rule.
	const roundedFull = nightlyRatesExperiment ? 'rounded-none' : 'rounded-full';
	const weekendCapsLTR = nightlyRatesExperiment
		? '[&>div.range-center]:bg-button-primary nth-[7n+1]:[&>div.range-center]:rounded-none nth-[7n]:[&>div.range-center]:rounded-none nth-[7n+1]:[&>.range-padding-left]:invisible nth-[7n]:[&>.range-padding-right]:invisible'
		: '[&>div.range-center]:bg-button-primary nth-[7n+1]:[&>div.range-center]:rounded-l-full nth-[7n]:[&>div.range-center]:rounded-r-full nth-[7n+1]:[&>.range-padding-left]:invisible nth-[7n]:[&>.range-padding-right]:invisible';
	const weekendCapsRTL = nightlyRatesExperiment
		? '[&>div.range-center]:bg-button-primary nth-[7n+1]:[&>div.range-center]:rounded-none nth-[7n]:[&>div.range-center]:rounded-none nth-[7n+1]:[&>.range-padding-right]:invisible nth-[7n]:[&>.range-padding-left]:invisible'
		: '[&>div.range-center]:bg-button-primary nth-[7n+1]:[&>div.range-center]:rounded-r-full nth-[7n]:[&>div.range-center]:rounded-l-full nth-[7n+1]:[&>.range-padding-right]:invisible nth-[7n]:[&>.range-padding-left]:invisible';

	const tileClassName = useMemo(() => {
		const args = { activeStartDate, date };

		return typeof tileClassNameProps === 'function' ? tileClassNameProps(args) : tileClassNameProps;
	}, [activeStartDate, date, tileClassNameProps]);

	const tileContent = useMemo(() => {
		const args = { activeStartDate, date };

		return typeof tileContentProps === 'function' ? tileContentProps(args) : tileContentProps;
	}, [activeStartDate, date, tileContentProps]);

	const isBeforeMinDate =
		minDate &&
		Math.floor(startOfDay(minDate).getTime() / 1000) >
			Math.floor(startOfDay(date).getTime() / 1000);
	const isSameOrAfterMaxDate =
		maxDate &&
		Math.floor(startOfDay(maxDate).getTime() / 1000) <=
			Math.floor(startOfDay(date).getTime() / 1000);
	const isDisabled =
		isBeforeMinDate ||
		isSameOrAfterMaxDate ||
		(tileDisabled && tileDisabled({ activeStartDate, date })) ||
		(!allowUnavailableSelection && isAvailable === false);
	const showRestrictedStyle = isRestricted && !isBeforeMinDate && !isSameOrAfterMaxDate;
	const showRestrictedDisabled = showRestrictedStyle && isDisabled;
	const isRTL = getRTL();
	const showStrikethrough =
		isAvailable === false &&
		!isSelected &&
		!isBeforeMinDate &&
		!isSameOrAfterMaxDate &&
		!isSameDay(date, new Date());

	return (
		<button
			className={cn(
				'max-h-[37px] h-[36px] flex items-center justify-center relative',
				isMobile && isRangeSelection ? 'w-full -mx-px' : 'w-[36px]',
				{
					'cursor-not-allowed': showRestrictedDisabled,
					'disabled:opacity-100': showRestrictedStyle,
					'disabled:hover:bg-transparent': showRestrictedDisabled,
					group: nightlyRatesExperiment,
					[weekendCapsLTR]: !isRTL && isRangeSelection && !isMobile,
					[weekendCapsRTL]: isRTL && isRangeSelection && !isMobile,
				},
				classes,
				tileClassName
			)}
			disabled={isDisabled}
			onClick={onClick ? event => onClick(date, event) : undefined}
			style={style}
			type="button"
			aria-label={tileAriaLabel?.(date)}
		>
			{!useMobileRangeLayout ? (
				<>
					<div
						className={cn('w-[calc((100%-36px)/2)] h-full', {
							'range-padding-left': !isRTL,
							'range-padding-right': isRTL,
						})}
					></div>
				</>
			) : null}
			<div
				className={cn(
					'text-label text-center flex justify-center items-center h-full range-center',
					useMobileRangeLayout ? 'w-full' : 'w-full w-[36px] min-w-[36px] h-[36px]',
					{
						[roundedFull]: isSelected && isSameDaySelection && !isDisabled,
						relative: (isSelected && !isDisabled) || showStrikethrough,
						'rounded-[100px] border border-dashed border-button-primary':
							showRestrictedStyle && !nightlyRatesExperiment && !isSelected,
						'bg-transparent': !isSelected || (isDisabled && !isSameDaySelection),
						[`bg-button-primary ${roundedFull}`]: isSelected && isSameDaySelection,
						'[@media(hover:hover)_and_(pointer:fine)]:hover:bg-[rgba(0,0,0,0.15)] [@media(hover:hover)_and_(pointer:fine)]:hover:rounded-full':
							!isDisabled && !isSelected,
					}
				)}
			>
				{formatAbbr ? (
					<abbr
						className={cn(
							'flex justify-center items-center w-full h-full text-center text-[12px] pl-[1.8px]',
							{
								'text-button-primary':
									!isSelected &&
									((showRestrictedStyle && !isDisabled) || (!isDisabled && isAvailable)),
								'text-button-primary opacity-70': !isSelected && showRestrictedDisabled,
								'text-button-primary-disabled':
									!(isSelected || showRestrictedStyle) && (isDisabled || !isAvailable),
								'text-button-primary-inverse': isSelected,
							}
						)}
						aria-label={formatAbbr(locale, date)}
					>
						{children}
					</abbr>
				) : (
					children
				)}
				{tileContent}
				{/* AC 5.3: strikethrough — centered inside the 36×36 px center div.
				    left-1/2 -translate-x-1/2 + top-1/2 -translate-y-1/2 = geometrically
				    centred: no offset math needed. */}
				{showStrikethrough && (
					<div
						data-testid="tile-strikethrough"
						className="pointer-events-none absolute top-[calc(50%-0.5px)] left-1/2 h-[1px] w-[30px] -translate-x-1/2 bg-button-primary-disabled"
					/>
				)}
			</div>
			{!useMobileRangeLayout ? (
				<div
					className={cn('w-[calc((100%-36px)/2)] h-full', {
						'range-padding-right': !isRTL,
						'range-padding-left': isRTL,
					})}
				></div>
			) : null}
			{showStrikethrough ? (
				<div
					className={cn('absolute top-[50%] left-0 w-full h-[1px] bg-button-primary-disabled', {
						'-translate-y-[5px]': nightlyRatesExperiment,
					})}
				/>
			) : null}
		</button>
	);
}
